import { DateTime } from "luxon";

/** The `sessionStorage` key holding when this tab last forced an update reload, in epoch milliseconds. */
export const LAST_FORCED_RELOAD_KEY = "idleUpdate.lastForcedReloadAt";

/** What the reload guard read from storage: a time, no time yet, or nothing it could trust. */
export type LastForcedReload = { readable: true; at: number | null } | { readable: false };

/** State the idle update gate branches on; see `ReloadPrompt` for how each value is derived. */
export type IdleUpdateState = {
  /** A new service worker is waiting: the "Your version is out of date." notice is showing. */
  needRefresh: boolean;
  enabled: boolean;
  /** No input for `idleMinutes`. */
  isIdle: boolean;
  /** Within `bootWindowSeconds` of a cold or warm boot. */
  bootWindowOpen: boolean;
  lastForcedReload: LastForcedReload;
  reloadGuardSeconds: number;
  now: number;
};

/**
 * Whether the tab applies the waiting build now or leaves the notice to the user.
 *
 * Applies when a build waits, the setting is on, the tab is idle or inside a boot window, and the guard can show no
 * forced reload within `reloadGuardSeconds`. A guard that cannot be read never forces, so a tab without storage can
 * never loop.
 */
export function resolveIdleUpdateAction(state: IdleUpdateState): "apply" | "wait" {
  if (!state.needRefresh || !state.enabled) return "wait";
  if (!state.isIdle && !state.bootWindowOpen) return "wait";
  if (!state.lastForcedReload.readable) return "wait";
  const { at } = state.lastForcedReload;
  const guardOpenedAt = DateTime.fromMillis(state.now).minus({ seconds: state.reloadGuardSeconds });
  if (at !== null && at > guardOpenedAt.toMillis()) return "wait";
  return "apply";
}

/** Reads the guard time; a missing or unparseable value reads as none, a throwing storage as unreadable. */
export function readLastForcedReload(storage: Pick<Storage, "getItem">): LastForcedReload {
  try {
    const raw = storage.getItem(LAST_FORCED_RELOAD_KEY);
    const at = raw === null ? Number.NaN : Number(raw);
    return { readable: true, at: Number.isFinite(at) ? at : null };
  } catch {
    return { readable: false };
  }
}

/** Records a forced reload; false when storage refuses, in which case the caller must not reload. */
export function writeLastForcedReload(storage: Pick<Storage, "setItem">, now: number): boolean {
  try {
    storage.setItem(LAST_FORCED_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
